import { Button, EmptyState } from '@devizy/ui';
import {
  Briefcase,
  ClockCounterClockwise,
  FilePlus,
  Megaphone,
  Plus,
  Receipt,
  RocketLaunch,
  Warning,
} from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

import { DashCard } from '@/components/dashboard/dash-card';
import { MetricCard } from '@/components/dashboard/metric-card';
import { PendingCommissionPayments } from '@/components/dashboard/pending-commission-payments';
import { QuickActionLink } from '@/components/dashboard/quick-action-link';
import { trpc } from '@/lib/trpc/client';

export const Route = createFileRoute('/_app/dashboard/requester')({
  component: RequesterDashboardPage,
});

function RequesterDashboardPage(): React.ReactElement {
  const { data: summary, isLoading } = trpc.requester.dashboardSummary.useQuery();
  const { data: me } = trpc.user.me.useQuery();
  const isAdmin = me?.isAdmin ?? false;
  const metric = (value: number | undefined): string =>
    isLoading || value === undefined ? '—' : String(value);

  return (
    <>
      <PendingCommissionPayments />
      <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard
          label="Offres actives"
          value={metric(summary?.activeOffers)}
          loading={isLoading}
          icon={<Megaphone className="size-5" weight="regular" />}
        />
        <MetricCard
          label="Devis reçus"
          value={metric(summary?.receivedQuotes)}
          loading={isLoading}
          icon={<Receipt className="size-5" weight="regular" />}
        />
        <MetricCard
          label="Projets en cours"
          value={metric(summary?.projectsInProgress)}
          loading={isLoading}
          icon={<Briefcase className="size-5" weight="regular" />}
        />
        <MetricCard
          label="Litiges ouverts"
          value={metric(summary?.openDisputes)}
          loading={isLoading}
          icon={<Warning className="size-5" weight="regular" />}
        />
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <DashCard
          className="lg:col-span-2"
          icon={<ClockCounterClockwise className="size-5" weight="regular" />}
          title="Activité récente"
          subtitle="Devis, projets et messages"
          showMenu
        >
          <EmptyState
            className="rounded-2xl"
            icon={<ClockCounterClockwise weight="regular" />}
            title="Aucune activité pour le moment"
            description={
              isAdmin
                ? 'Consultez les offres publiées sur la plateforme.'
                : 'Crée une offre pour voir les devis arriver et démarrer le suivi de projet.'
            }
            action={
              isAdmin ? (
                <Button asChild variant="outline">
                  <Link to="/offers" className="gap-2">
                    <Megaphone weight="bold" className="size-4" />
                    Voir les offres
                  </Link>
                </Button>
              ) : (
                <Button asChild>
                  <Link to="/offers/new" search={{ draftId: undefined }} className="gap-2">
                    <Plus weight="bold" className="size-4" />
                    Nouvelle offre
                  </Link>
                </Button>
              )
            }
          />
        </DashCard>

        <DashCard
          icon={<RocketLaunch className="size-5" weight="regular" />}
          title="Démarrage rapide"
          subtitle="Les actions clés"
        >
          <div className="flex flex-col gap-2">
            {isAdmin ? (
              <QuickActionLink
                href="/offers"
                icon={<Megaphone className="size-5" weight="regular" />}
                label="Voir les offres"
                hint="Toutes les offres de la plateforme"
              />
            ) : (
              <QuickActionLink
                href="/offers/new"
                icon={<FilePlus className="size-5" weight="regular" />}
                label="Créer une offre"
                hint="CDC structuré par IA"
              />
            )}
            <QuickActionLink
              href="/projects"
              icon={<Briefcase className="size-5" weight="regular" />}
              label="Voir mes projets"
              hint="Suivi tâche par tâche"
            />
          </div>
        </DashCard>
      </section>
    </>
  );
}
